import { FC, useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useLoaderBusinessUnit } from "../../hooks/businessUnit/useLoaderBusinessUnit";
import DataGridBuckets from "../../components/organismos/buckets/DataGridBuckets";
import {
  Alert,
  Container,
  Snackbar,
  SnackbarCloseReason,
  styled,
  Typography,
} from "@mui/material";
import BucketFilterController from "../../components/moleculas/bucket/FilterController";
import BucketActionsController from "../../components/moleculas/bucket/ActionsController";
import { useAppDispatch, useAppSelector } from "../../store/hook/store.hook";
import { getChargeBuckets } from "../../store/actions/buckets.action";
import { clearError } from "../../store/slices/project.slice";
import {
  clearFilters,
  setFilters,
} from "../../store/slices/bucketFilters.slice";

const Titled = styled(Typography)(() => ({
  textTransform: "capitalize",
}));

const BusinessUnit: FC = () => {
  const dispatch = useAppDispatch();
  const { state } = useLocation();
  const { loadBuckets, paginated } = useLoaderBusinessUnit();
  const { error } = useAppSelector((state) => state.projects);
  const filters = useAppSelector((state) => state.bucketFilters);

  const [openMessage, setOpenMessage] = useState<boolean>(false);

  const handleDateRange = (val: any) => {
    const [newFrom, newTo] = val;

    dispatch(
      setFilters({
        ...filters,
        from: newFrom && newFrom.format("YYYY-MM-DD"), // Guarda null en Redux para valores vacíos
        to: newTo && newTo.format("YYYY-MM-DD"),
      })
    );
  };

  const handleFilterChange = (field: keyof typeof filters, value: any) => {
    const updatedFilters = {
      ...filters,
      [field]: value,
    };

    dispatch(setFilters(updatedFilters));
  };

  useMemo(() => {
    const timeoutId = setTimeout(() => filters, 500);
    return () => clearTimeout(timeoutId);
  }, [filters]);

  useEffect(() => {
    loadBuckets(state.businessUnit.id);
  }, [state.businessUnit.id, loadBuckets]);

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(
        getChargeBuckets({
          businessUnit: state.businessUnit.id,
          pagination: {
            page: paginated!.page + 1,
            limit: paginated!.limit,
          },
          search: filters.search,
          project_state: filters.project_state,
          from: filters.from || undefined,
          to: filters.to || undefined,
        })
      );
    }, 300);

    return () => clearTimeout(timer);
  }, [dispatch, state.businessUnit.id, paginated, filters]);

  useEffect(() => {
    setOpenMessage(!!error);
  }, [error]);

  const handleClose = (
    _: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    dispatch(clearError());
    if (reason === "clickaway") return;
    setOpenMessage(false);
  };

  const handleClearAllFilters = () => {
    dispatch(clearFilters()); // Usa la acción clearFilters del slice
  };

  return (
    <Container>
      <Titled variant="h3">{state.businessUnit.name}</Titled>
      <BucketFilterController
        search={filters.search}
        setSearch={(val: string) => handleFilterChange("search", val)}
        project_state={filters.project_state}
        setProjectState={(val: string) =>
          handleFilterChange("project_state", val)
        }
        from={filters.from || ""}
        to={filters.to || ""}
        onHandleDateRange={handleDateRange}
        onClearAllFilters={handleClearAllFilters} // Pasa el handler de limpieza
      />

      <BucketActionsController />
      <DataGridBuckets />
      <Snackbar
        open={openMessage}
        autoHideDuration={5000}
        onClose={handleClose}
        message={error}
      >
        <Alert
          onClose={handleClose}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default BusinessUnit;
